import { useMemo, useState } from 'react'
import { Box } from '@mui/material'
import {
  SchedulerFormatChangeoverRowType,
  SchedulerResourceAndMaterialConstraintsRowType,
  SchedulerTabsProps,
} from './Scheduler.types'
import {
  useScheduleProductionRequirements,
  useSchedulerTableHandlers,
} from './Scheduler.data'
import { SchedulerCleningMappingTable } from './SchedulerCleaningMapping.muiTable'
import { SchedulerFormatChangeoverTable } from './SchedulerFormatChangeover.muiTable'
import { FONT_SANS, TogSwitch, TOKENS } from './SchedulerDesign'
import { TLFilterSelect } from './SchedulerFilterSelect'

export const ScheduleCleaningMapping = ({
  schedulerId,
  iteration,
}: SchedulerTabsProps) => {
  const API = 'cleaning-mapping'
  const FORMAT_API = 'format-changeover'

  const { data: responseData = { data: [], predicate: undefined }, isLoading } =
    useScheduleProductionRequirements(schedulerId, API, iteration)
  const { data = [], predicate } = responseData

  const {
    updateTableData,
    uploadScheduleData,
    deleteTableData,
    isSaving,
    isUploading,
    isDeleting,
    updateRowsStatus,
  } = useSchedulerTableHandlers(schedulerId, API, iteration)

  // ── Format changeovers ────────────────────────────────────────────────
  // Authored here behind a toggle, the same way trials are authored on the
  // downtime tab: it is a changeover rule rather than a cleaning rule, but it
  // is where the client asked to type it. Its own Delta table
  // (`FormatChangeoverDelta`) — cleaning_mapping merges on `asset`, so a
  // format row there would have nowhere to live.
  //
  // Defaults to hidden so the cleaning flow stays uncluttered, and defaults to
  // SHOWN when the schedule already has format rows, so configured data never
  // sits invisible. The toggle always wins over that default. `null` means "no
  // explicit choice yet, use the default".
  const [formatOverride, setFormatOverride] = useState<boolean | null>(null)

  const {
    data: formatResponse = { data: [], predicate: undefined },
    isLoading: formatLoading,
  } = useScheduleProductionRequirements(schedulerId, FORMAT_API, iteration)
  const serverFormatRows = (formatResponse.data ??
    []) as SchedulerFormatChangeoverRowType[]
  const hasServerFormatRows = serverFormatRows.length > 0
  const showFormats = formatOverride ?? hasServerFormatRows

  const formatHandlers = useSchedulerTableHandlers<
    SchedulerFormatChangeoverRowType
  >(schedulerId, FORMAT_API, iteration)

  const [fromFilter, setFromFilter] = useState<string[]>([])
  const [toFilter, setToFilter] = useState<string[]>([])
  const [onlyConfigured, setOnlyConfigured] = useState(false)
  // The format vocabulary comes from the products themselves — the master-data
  // `Format` column is the only place a format is declared, so the from/to
  // pickers offer exactly the distinct values in use. Nothing else defines a
  // format, which is why a product with a typo in its Format simply shows up
  // here as a format of its own.
  //
  // Fetched only once the format table is on screen; both fetches are
  // irrelevant to the cleaning flow.
  const { data: masterResponse } = useScheduleProductionRequirements(
    schedulerId,
    'product-master-data',
    iteration,
    undefined,
    showFormats
  )
  const formatOptions = useMemo(() => {
    const rows = (masterResponse?.data ?? []) as { format?: string | null }[]
    const names = new Set<string>()
    for (const r of rows) {
      const f = String(r?.format ?? '').trim()
      // Blank is the normal case — most products have no format — and must
      // not become an option.
      if (f) names.add(f)
    }
    return Array.from(names).sort()
  }, [masterResponse])

  // Resource constraints, for the Resource Constraint column: a format change
  // can consume a resource while it runs, which is how reconfiguring one line
  // takes down a sibling that shares the same equipment.
  const { data: constraintsResponse } = useScheduleProductionRequirements(
    schedulerId,
    'resource-and-material-constraints',
    iteration,
    undefined,
    showFormats
  )
  const resourceOptions = useMemo(() => {
    const rows = (constraintsResponse?.data ??
      []) as SchedulerResourceAndMaterialConstraintsRowType[]
    const names = new Set<string>()
    for (const r of rows) {
      if (r?.ResourceConstraint) names.add(String(r.ResourceConstraint))
    }
    return Array.from(names).sort()
  }, [constraintsResponse])

  // Every ordered pair of declared formats, blank, with anything saved
  // overlaid. The stored table only holds the pairs someone configured —
  // saving sends changed rows only — but a planner cannot fill in a grid that
  // starts empty, and cannot discover which pairs exist.
  //
  // A blank row is inert in the engine (no time, no cost → skipped at parse),
  // so scaffolding costs nothing. Note this is now a small grid: it is the
  // square of the number of FORMATS, not of the ~28 resource constraints the
  // pickers used to offer.
  //
  // Synthetic ids are negative and derived from position, so they are stable
  // across renders and cannot collide with a server row's id.
  // Rows added by hand with "Add changeover", before they are saved.
  //
  // The scaffold below only enumerates pairs of formats some product already
  // declares, which leaves two gaps this fills: a schedule where the Format
  // column has not been filled in yet has NO pairs and so no way in at all,
  // and a planner may want to price a transition before assigning that format
  // to any product. Same shape as the trials tab's `newTrials`.
  const [newFormatRows, setNewFormatRows] = useState<
    SchedulerFormatChangeoverRowType[]
  >([])

  // Ids for hand-added rows. Offset far below the scaffold's `-slot` ids (which
  // run -1..-(formats²)) so the two can never collide — BaseTable keys rows by
  // id, and a collision would silently drop one of them.
  const MANUAL_ID_BASE = -100000
  const addFormatRow = () => {
    setNewFormatRows((rows) => [
      ...rows,
      {
        id: MANUAL_ID_BASE - rows.length,
        format_from: '',
        format_to: '',
        changeover_time: '0',
        changeover_cost: '0',
        resource_constraint: null,
        resource_amount: null,
      },
    ])
  }
  // From/to are the merge key, so they may only be edited on a row that has
  // not been saved or scaffolded — see `isCellEditable` below.
  const manualIds = useMemo(
    () => new Set(newFormatRows.map((r) => r.id)),
    [newFormatRows]
  )

  const formatRows = useMemo(() => {
    const bySlot = new Map<string, SchedulerFormatChangeoverRowType>()
    for (const r of serverFormatRows) {
      bySlot.set(`${r.format_from}->${r.format_to}`, r)
    }
    const out: SchedulerFormatChangeoverRowType[] = []
    let slot = 0
    for (const from of formatOptions) {
      for (const to of formatOptions) {
        if (from === to) continue
        slot += 1
        const existing = bySlot.get(`${from}->${to}`)
        out.push(
          existing ?? {
            id: -slot,
            format_from: from,
            format_to: to,
            changeover_time: '0',
            changeover_cost: '0',
            resource_constraint: null,
            resource_amount: null,
          }
        )
        bySlot.delete(`${from}->${to}`)
      }
    }
    // A saved row naming a format no product declares any more stays visible —
    // it is live data the engine still reads (and reports as unreachable in
    // its own diagnostics), and hiding it would make a stale row impossible to
    // find or clear.
    for (const leftover of bySlot.values()) out.push(leftover)
    // Hand-added rows last, so a new blank row appears at the bottom where the
    // button that created it is.
    return [...out, ...newFormatRows]
  }, [serverFormatRows, formatOptions, newFormatRows])

  // Matches the engine's inert test exactly: a row with neither a time nor a
  // cost is skipped at parse, whatever else is on it. A resource named on a
  // zero-duration row consumes nothing, so it does not make the row count.
  const isConfigured = (r: SchedulerFormatChangeoverRowType) =>
    Number(r.changeover_time) > 0 || Number(r.changeover_cost) > 0

  const visibleFormatRows = useMemo(
    () =>
      formatRows.filter((r) => {
        if (fromFilter.length && !fromFilter.includes(r.format_from)) {
          return false
        }
        if (toFilter.length && !toFilter.includes(r.format_to)) return false
        if (onlyConfigured && !isConfigured(r)) return false
        return true
      }),
    [formatRows, fromFilter, toFilter, onlyConfigured]
  )
  const configuredCount = useMemo(
    () => formatRows.filter(isConfigured).length,
    [formatRows]
  )
  const filterOptions = useMemo(
    () => formatOptions.map((f) => ({ value: f, label: f })),
    [formatOptions]
  )

  return (
    <Box
      sx={{
        height: 'calc(100vh - 240px)',
        minHeight: 400,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        title={
          showFormats
            ? 'Showing bottle-format changeovers. Switch off to view the cleaning mapping table.'
            : hasServerFormatRows
              ? 'This schedule has format changeovers. Switch on to view them.'
              : 'Switch on to show the bottle-format changeover table: the time and cost of moving a line between formats.'
        }
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          mb: 1.5,
          flexShrink: 0,
        }}
      >
        <TogSwitch on={showFormats} onChange={setFormatOverride} />
        <Box
          component="button"
          onClick={() => setFormatOverride(!showFormats)}
          sx={{
            border: 'none',
            background: 'none',
            p: 0,
            fontSize: 12,
            fontWeight: 700,
            color: showFormats ? TOKENS.ink700 : TOKENS.ink500,
            fontFamily: FONT_SANS,
            cursor: 'pointer',
          }}
        >
          Format changeovers
        </Box>
      </Box>

      {showFormats && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            mb: 1.5,
            flexWrap: 'wrap',
            flexShrink: 0,
          }}
        >
          <TLFilterSelect
            label="From"
            value={fromFilter}
            onChange={setFromFilter}
            options={filterOptions}
          />
          <TLFilterSelect
            label="To"
            value={toFilter}
            onChange={setToFilter}
            options={filterOptions}
          />
          <Box
            component="button"
            onClick={() => setOnlyConfigured((v) => !v)}
            sx={{
              height: 24,
              px: '9px',
              border: `1px solid ${
                onlyConfigured ? TOKENS.stateComplete : TOKENS.line300
              }`,
              borderRadius: '2px',
              bgcolor: onlyConfigured ? 'rgba(14,124,134,0.08)' : '#fff',
              color: onlyConfigured ? TOKENS.stateComplete : TOKENS.ink500,
              fontFamily: FONT_SANS,
              fontSize: 10.5,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Only configured
          </Box>
          <Box
            sx={{
              fontFamily: FONT_SANS,
              fontSize: 10.5,
              color: TOKENS.ink400,
              whiteSpace: 'nowrap',
            }}
          >
            {visibleFormatRows.length} of {formatRows.length} shown ·{' '}
            {configuredCount} configured
          </Box>
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {showFormats ? (
          <SchedulerFormatChangeoverTable
            data={visibleFormatRows}
            isLoading={formatLoading}
            updateTableData={formatHandlers.updateTableData}
            uploadScheduleData={formatHandlers.uploadScheduleData}
            isUploading={formatHandlers.isUploading}
            isSaving={formatHandlers.isSaving}
            updateRowsStatus={formatHandlers.updateRowsStatus}
            predicate={formatResponse.predicate}
            deleteTableData={formatHandlers.deleteTableData}
            isDeleting={formatHandlers.isDeleting}
            formatOptions={formatOptions}
            resourceOptions={resourceOptions}
            onAddRow={addFormatRow}
            manualIds={manualIds}
          />
        ) : (
          <SchedulerCleningMappingTable
            data={data}
            isLoading={isLoading}
            updateTableData={updateTableData}
            uploadScheduleData={uploadScheduleData}
            isUploading={isUploading}
            isSaving={isSaving}
            updateRowsStatus={updateRowsStatus}
            predicate={predicate}
            deleteTableData={deleteTableData}
            isDeleting={isDeleting}
          />
        )}
      </Box>
    </Box>
  )
}
